export type CreditCardInvoiceStatusV2 =
  | "open"
  | "closed"
  | "partially_paid"
  | "paid"
  | "overdue";

export type CreditCardInvoiceItemTypeV2 =
  | "purchase"
  | "refund"
  | "adjustment";

export type CreditCardInvoiceItemV2 = {
  id: string;
  amount: number | string;
  type?: CreditCardInvoiceItemTypeV2 | null;
  status?: string | null;
};

export type CreditCardInvoicePaymentStatusV2 =
  | "pending"
  | "completed"
  | "reversed"
  | "failed"
  | "cancelled";

export type CreditCardInvoicePaymentOriginV2 =
  | "account"
  | "manual"
  | "import"
  | "legacy"
  | "adjustment";

export type CreditCardInvoicePaymentV2 = {
  id: string;
  invoiceId: string;
  amount: number | string;
  date: string;
  accountId: string | null;
  effectiveDate: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  status: CreditCardInvoicePaymentStatusV2;
  origin: CreditCardInvoicePaymentOriginV2;
  transactionId: string | null;
};

export type CreditCardInvoiceV2 = {
  id: string;
  totalAmount?: number | string | null;
  status?: string | null;
  closingDate: string;
  dueDate: string;
  paidAt?: string | null;
  paidAmount?: number | string | null;
  paidFromAccountId?: string | null;
  paidTransactionId?: string | null;
  userId: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type CreditCardInvoiceSummaryV2 = {
  invoiceId: string;
  totalCharged: number;
  purchaseTotal: number;
  refundTotal: number;
  adjustmentTotal: number;
  paidAmount: number;
  outstandingBalance: number;
  creditBalance: number;
  status: CreditCardInvoiceStatusV2;
  paymentHistory: CreditCardInvoicePaymentV2[];
  paymentCount: number;
  completedPaymentCount: number;
  nextDueDate: string | null;
  minimumPayment: number | null;
  lastPaymentAt: string | null;
  usesLegacyPaymentSnapshot: boolean;
};

function toCents(value: number | string | null | undefined) {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) throw new Error("invalid_credit_card_amount");
  return Math.round(amount * 100);
}

function fromCents(value: number) {
  return Number((value / 100).toFixed(2));
}

function itemType(item: CreditCardInvoiceItemV2): CreditCardInvoiceItemTypeV2 {
  if (item.type) return item.type;
  return toCents(item.amount) < 0 ? "refund" : "purchase";
}

function paymentTimestamp(payment: CreditCardInvoicePaymentV2) {
  return payment.effectiveDate ?? payment.date ?? payment.createdAt;
}

function comparePayments(
  left: CreditCardInvoicePaymentV2,
  right: CreditCardInvoicePaymentV2
) {
  return (
    paymentTimestamp(left).localeCompare(paymentTimestamp(right)) ||
    left.createdAt.localeCompare(right.createdAt) ||
    left.id.localeCompare(right.id)
  );
}

export function buildLegacyInvoicePayment(
  invoice: CreditCardInvoiceV2
): CreditCardInvoicePaymentV2 | null {
  if (invoice.status !== "paid") return null;
  const amount = toCents(invoice.paidAmount ?? invoice.totalAmount);
  if (amount <= 0) return null;
  const effectiveDate =
    invoice.paidAt ?? invoice.updatedAt ?? invoice.createdAt ?? invoice.dueDate;

  return {
    id: `legacy:${invoice.id}`,
    invoiceId: invoice.id,
    amount: fromCents(amount),
    date: effectiveDate.slice(0, 10),
    accountId: invoice.paidFromAccountId ?? null,
    effectiveDate,
    notes: "Legacy invoice payment snapshot",
    createdBy: invoice.userId,
    createdAt: effectiveDate,
    updatedAt: effectiveDate,
    status: "completed",
    origin: "legacy",
    transactionId: invoice.paidTransactionId ?? null,
  };
}

export function resolveInvoicePaymentHistory(
  invoice: CreditCardInvoiceV2,
  payments: CreditCardInvoicePaymentV2[]
) {
  const hasNativePayment = payments.some(payment => payment.origin !== "legacy");
  if (hasNativePayment) {
    return {
      payments: [...payments].sort(comparePayments),
      usesLegacyPaymentSnapshot: false,
    };
  }

  const staleLegacyPayments = payments.map(payment => ({
    ...payment,
    status:
      payment.status === "completed"
        ? ("reversed" as const)
        : payment.status,
  }));
  const legacy = buildLegacyInvoicePayment(invoice);
  const matchingLegacyPayment = legacy
    ? payments.find(payment => {
        if (
          payment.transactionId &&
          invoice.paidTransactionId
        ) {
          return payment.transactionId === invoice.paidTransactionId;
        }
        return (
          paymentTimestamp(payment) === paymentTimestamp(legacy) &&
          toCents(payment.amount) === toCents(legacy.amount)
        );
      })
    : null;

  return {
    payments: (
      matchingLegacyPayment
        ? payments
        : [...staleLegacyPayments, ...(legacy ? [legacy] : [])]
    ).sort(comparePayments),
    usesLegacyPaymentSnapshot: payments.length > 0 || Boolean(legacy),
  };
}

function lifecycleStatus(
  invoice: CreditCardInvoiceV2,
  referenceDate: string
): "open" | "closed" | "overdue" {
  if (referenceDate > invoice.dueDate) return "overdue";
  if (referenceDate >= invoice.closingDate) return "closed";
  return "open";
}

export function calculateCreditCardInvoiceSummary({
  invoice,
  items,
  payments,
  referenceDate = new Date().toISOString().slice(0, 10),
}: {
  invoice: CreditCardInvoiceV2;
  items: CreditCardInvoiceItemV2[];
  payments: CreditCardInvoicePaymentV2[];
  referenceDate?: string;
}): CreditCardInvoiceSummaryV2 {
  let purchaseCents = 0;
  let refundCents = 0;
  let adjustmentCents = 0;

  for (const item of items) {
    if (item.status === "cancelled") continue;
    const amount = toCents(item.amount);
    switch (itemType(item)) {
      case "refund":
        refundCents += Math.abs(amount);
        break;
      case "adjustment":
        adjustmentCents += amount;
        break;
      default:
        purchaseCents += amount;
    }
  }

  const itemTotalCents = Math.max(
    0,
    purchaseCents - refundCents + adjustmentCents
  );
  const totalCents =
    items.length > 0 ? itemTotalCents : Math.max(0, toCents(invoice.totalAmount));
  const paymentHistory = resolveInvoicePaymentHistory(invoice, payments);
  const completedPayments = paymentHistory.payments.filter(
    payment => payment.status === "completed"
  );
  const paidCents = completedPayments.reduce(
    (total, payment) => total + toCents(payment.amount),
    0
  );
  const outstandingCents = Math.max(0, totalCents - paidCents);
  const creditCents = Math.max(0, paidCents - totalCents);
  const status: CreditCardInvoiceStatusV2 =
    paidCents > 0 && outstandingCents > 0
      ? "partially_paid"
      : paidCents > 0 && outstandingCents === 0
        ? "paid"
        : lifecycleStatus(invoice, referenceDate);

  return {
    invoiceId: invoice.id,
    totalCharged: fromCents(totalCents),
    purchaseTotal: fromCents(purchaseCents),
    refundTotal: fromCents(refundCents),
    adjustmentTotal: fromCents(adjustmentCents),
    paidAmount: fromCents(paidCents),
    outstandingBalance: fromCents(outstandingCents),
    creditBalance: fromCents(creditCents),
    status,
    paymentHistory: paymentHistory.payments,
    paymentCount: paymentHistory.payments.length,
    completedPaymentCount: completedPayments.length,
    nextDueDate: outstandingCents > 0 ? invoice.dueDate : null,
    minimumPayment: null,
    lastPaymentAt:
      completedPayments.length > 0
        ? paymentTimestamp(completedPayments.at(-1)!)
        : null,
    usesLegacyPaymentSnapshot: paymentHistory.usesLegacyPaymentSnapshot,
  };
}
